import { logger } from 'affiny-core/logger';
import { handleAsyncStatus } from 'affiny-core/modules/asynchronousState/saga';
import { EAsyncEventTypes } from 'affiny-core/modules/asynchronousState/types';
import { selectAccountId } from 'affiny-core/redux/selectors/accountSelectors';
import { selectAffinyMatchList } from 'affiny-core/redux/selectors/matchesSelectors';
import { selectIsSub } from 'affiny-core/redux/selectors/subscriptionsSelectors';
import {
  selectMemberByThreadId,
  selectThreadCurrentPage,
  selectThreadIdByOtherAccountId,
  selectThreadTotalItem,
} from 'affiny-core/redux/selectors/threadsSelectors';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { HTTP_STATUS } from '../../helpers/AsyncStatusHandler';
import { EAffinyEventOrigin } from '../api/core/affiny.eventorigins';
import { EThreadPermission, IThread } from '../api/models/thread/thread.types';
import { NormalizedMessages } from '../api/repositories/messages/messages.normalizer';
import {
  getMessages,
  PAGE_SIZE,
  postMessage,
} from '../api/repositories/messages/messages.sagas';
import { postDryRun } from '../api/repositories/thread/thread.sagas';
import { ActionsOfType, updateCurrentPage } from '../helpers';
import { checkHasCompletedProfile } from '../helpers/checkSaga';
import { matchesActions } from '../matches/actions';
import { getMemberWithMainPicture } from '../members/saga';
import { threadActions } from '../threads/actions';
import { threadSerializer } from '../threads/serializers';
import {
  EMessagesActionType,
  messagesActions,
  TMessagesActions,
} from './actions';
import {
  IMessageData,
  messageSerializer,
  unsentMessageSerializer,
} from './serializers';

export function* sendMessageSaga(
  senderAboId: string,
  receiverAboId: string,
  content: string,
  eventOrigin: EAffinyEventOrigin
) {
  if (!(yield* checkHasCompletedProfile())) return;

  try {
    return yield call(postMessage, {
      senderAboId,
      receiverAboId,
      content,
      eventOrigin,
    });
  } catch (error) {
    return unsentMessageSerializer(senderAboId, receiverAboId, content, error);
  }
}

export function* postMessageSaga(
  action: ActionsOfType<TMessagesActions, EMessagesActionType.POST_MESSAGE>
) {
  if (!(yield* checkHasCompletedProfile())) return;

  const { receiverAboId, content, unsentId, eventOrigin } = action.payload;

  const threadId = yield select(selectThreadIdByOtherAccountId, receiverAboId);

  try {
    const senderAboId = yield select(selectAccountId);

    if (unsentId) {
      yield put(messagesActions.deleteMessage(unsentId, threadId));
    }

    const response: IMessageData = yield call(
      sendMessageSaga,
      senderAboId,
      receiverAboId,
      content,
      eventOrigin
    );

    yield put(messagesActions.postMessageSuccess(response.data));
    const { mainPicture } = yield call(getMemberWithMainPicture, receiverAboId);

    yield put(
      threadActions.addMessageToThread({
        threadId: threadId || response.data.attributes.threadId,
        messageId: response.data.id,
        accountId: receiverAboId,
        mainPicture,
      })
    );

    const affinyMatchList = yield select(selectAffinyMatchList);

    if (affinyMatchList && affinyMatchList.includes(receiverAboId)) {
      yield put(matchesActions.removeUserAffinyMatches(receiverAboId));
    }

    yield put(
      messagesActions.postDryRun({
        threadId: threadId || response.data.attributes.threadId,
      })
    );

    const { unsentError } = response.data.attributes;

    if (unsentError) throw unsentError;
  } catch (error) {
    console.error(error);
    logger.logWithMessageAndData('postDryRun error', error);
  }
}

export function* getMessagesFrom({
  threadId,
  reset,
}: {
  threadId: string;
  reset: boolean;
}) {
  if (!(yield* checkHasCompletedProfile())) return;

  const currentPage: number = yield select(selectThreadCurrentPage, threadId);
  const numberOfMessages: number = yield select(
    selectThreadTotalItem,
    threadId
  );
  const updatedCurrentPage = updateCurrentPage({
    currentPage,
    numberOfItems: numberOfMessages,
    pageSize: PAGE_SIZE,
    reset,
  });

  if (!updatedCurrentPage) return;

  const otherMember = yield select(selectMemberByThreadId, threadId);

  const {
    entities,
    result: {
      meta: {
        pagination: { current_page, total },
      },
    },
  }: NormalizedMessages = yield call(
    getMessages,
    otherMember?.id,
    updatedCurrentPage
  );

  return { entities, meta: { current_page, total } };
}

export function* getMoreMessagesSaga(
  action: ActionsOfType<TMessagesActions, EMessagesActionType.GET_MORE>
) {
  if (!(yield* checkHasCompletedProfile())) return;

  const { accountId } = action.payload;
  const threadId = yield select(selectThreadIdByOtherAccountId, accountId);
  const newMessages = yield call(getMessagesFrom, {
    threadId: threadId,
    reset: false,
  });

  if (!newMessages) return;

  const { entities, meta } = newMessages;

  yield put(
    messagesActions.getMoreMessagesSuccess({
      entities,
      meta,
      threadId: threadId,
    })
  );
}

export function* checkPostMessagePermission(
  action: ActionsOfType<TMessagesActions, EMessagesActionType.POST_DRY_RUN>
) {
  const {
    payload: { threadId },
  } = action;

  if (!(yield* checkHasCompletedProfile())) return;

  try {
    yield call(postDryRun, threadId);
  } catch (error) {
    // TODO remove this
    if (error.status === HTTP_STATUS.PAYMENT_REQUIRED) {
      yield put(
        threadActions.checkPermissionError(
          threadId,
          EThreadPermission.CAN_POST_MESSAGE
        )
      );
    }

    throw error;
  }
}

export function* processNewMessageFromWebsocket(
  action: ActionsOfType<
    TMessagesActions,
    EMessagesActionType.NEW_MESSAGE_FROM_WEBSOCKET
  >
) {
  if (!(yield* checkHasCompletedProfile())) return;

  const myAccountId = yield select(selectAccountId);
  const isSub = yield select(selectIsSub);
  const message = yield call(messageSerializer, action.payload, myAccountId);

  yield put(messagesActions.newMessage(message));

  // Instantiate thread with the new message
  const senderAccountId = message.attributes.myAccountId;
  const { mainPicture } = yield call(getMemberWithMainPicture, senderAccountId);
  const threadId: string = yield select(
    selectThreadIdByOtherAccountId,
    senderAccountId
  );
  const thread: IThread = yield call(threadSerializer, action.payload, {
    accountId: senderAccountId,
    threadMainPicture: mainPicture,
    threadId,
    messageId: message.id,
  });

  // Check whether the user is reg or sub to only add a call when necessary
  // Get the thread to trigger the calls to check the breakers status
  if (!isSub) {
    yield put(threadActions.getThread(senderAccountId));
  }

  yield put(threadActions.newThreadFromWebsocket(thread));
}

// TODO : check if we can merge the yield alls
export function* messagesRootSaga() {
  yield all([
    takeLatest(
      EMessagesActionType.POST_MESSAGE,
      handleAsyncStatus(postMessageSaga, EAsyncEventTypes.POST_MESSAGE)
    ),
    takeLatest(
      EMessagesActionType.POST_DRY_RUN,
      handleAsyncStatus(
        checkPostMessagePermission,
        EAsyncEventTypes.POST_DRY_RUN
      )
    ),
  ]);
  yield all([
    takeLatest(
      EMessagesActionType.GET_MORE,
      handleAsyncStatus(getMoreMessagesSaga, EAsyncEventTypes.GET_MORE_MESSAGES)
    ),
  ]);
}
